import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '~/contexts';
import { Icon } from '~/components';
import { colors, weights } from '~/styles';
import { BackLink, EmptyState, Mono, Table, Td, Th } from '../ai/replay/shared';
import WorkspaceDisplay from '../ai/replay/WorkspaceDisplay';
import ActorCell from './ActorCell';
import TraceTimeline from './TraceTimeline';
import { buildDiffs } from './traceUtils';

const HeaderTable = styled(Table)`
  margin-bottom: 1.5rem;
`;

const Source = styled.span`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.8125rem;
  font-weight: ${weights.medium};
  color: ${colors.black};
`;

const STATUS_COLORS = {
  succeeded: colors.success,
  failed: colors.danger,
  running: colors.warning,
  pending: colors.grey40,
};

const StatusBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $status }) => STATUS_COLORS[$status] || colors.grey40};
  background: ${({ $status }) => (STATUS_COLORS[$status] || colors.grey40) + '15'};
  padding: 0.125rem 0.5rem;
  border-radius: 0.125rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
  margin: 1.5rem 0 0.75rem;
`;

const DiffCard = styled.div`
  border: solid 1px ${colors.grey10};
  border-radius: 0.375rem;
  overflow: hidden;

  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }
`;

const DiffHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background: ${colors.grey5};
  border-bottom: solid 1px ${colors.grey10};
  font-size: 0.8125rem;
  font-weight: ${weights.bold};
`;

const KIND_COLORS = {
  created: colors.success,
  updated: colors.warning,
  deleted: colors.danger,
};

const KindBadge = styled.span`
  font-size: 0.625rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $kind }) => KIND_COLORS[$kind] || colors.grey40};
  background: ${({ $kind }) => (KIND_COLORS[$kind] || colors.grey40) + '15'};
  padding: 0.0625rem 0.375rem;
  border-radius: 0.125rem;
`;

const DiffTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const DiffTh = styled.th`
  text-align: left;
  padding: 0.5rem 0.75rem;
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  color: ${colors.grey55};
  text-transform: uppercase;
  border-bottom: solid 1px ${colors.grey10};
  width: ${({ $width }) => $width || 'auto'};
`;

const DiffTd = styled.td`
  padding: 0.5rem 0.75rem;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.75rem;
  vertical-align: top;
  border-bottom: solid 1px ${colors.grey10};
  word-break: break-word;
  white-space: pre-wrap;
  color: ${({ $tone }) => ($tone === 'before' ? colors.danger : $tone === 'after' ? colors.success : colors.black)};
`;

const Unchanged = styled.span`
  color: ${colors.grey40};
`;

// The snapshotted entity id, linked to its cross-trace history in entity replay.
const EntityLink = styled(Link)`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.75rem;
  color: ${colors.grey55};

  &:hover {
    color: ${colors.primary};
  }
`;

// A no-op change reads as a compact note on the right of the header rather than an
// empty table block.
const HeaderNote = styled.span`
  margin-left: auto;
  font-size: 0.6875rem;
  font-weight: ${weights.normal};
  font-style: italic;
  color: ${colors.grey55};
  text-transform: none;
`;

function formatValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function TraceDetail() {
  const { traceId } = useParams();
  const api = useApi();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchTrace = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data: result } = await api.admin.traceReplay.getTrace(traceId);
        if (!cancelled) setData(result);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTrace();
    return () => {
      cancelled = true;
    };
  }, [api, traceId]);

  const diffs = useMemo(() => (data?.events ? buildDiffs(data.events) : []), [data]);

  return (
    <>
      {isLoading && <EmptyState>Loading trace...</EmptyState>}

      {!isLoading && !data && <EmptyState>Trace not found.</EmptyState>}

      {!isLoading && data && (
        <>
          {/* Return to the list with its filters intact — TraceList stashes its
              query string on every change so the back link restores it. */}
          <BackLink as={Link} to={`/admin/traces${sessionStorage.getItem('admin_traces_search') ?? ''}`}>
            <Icon icon="arrow-left" />
            Back to traces
          </BackLink>

          <HeaderTable>
            <thead>
              <tr>
                <Th>Trace</Th>
                <Th>Workspace</Th>
                <Th>Actor</Th>
                <Th>Status</Th>
                <Th>Started</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td>
                  <Source>{data.trace.source || '-'}</Source>
                  <div>
                    <Mono>{traceId}</Mono>
                  </div>
                </Td>
                <Td>
                  <WorkspaceDisplay workspace={data.workspace} />
                </Td>
                <Td>
                  <ActorCell
                    actor={data.trace.actor}
                    member={data.member}
                    apiKeyId={data.trace.apiKeyId}
                    admin={data.admin}
                  />
                </Td>
                <Td>
                  <StatusBadge $status={data.trace.status}>{data.trace.status}</StatusBadge>
                </Td>
                <Td title={data.trace.startedAt}>{moment(data.trace.startedAt).format('MMM D, YYYY h:mm a')}</Td>
              </tr>
            </tbody>
          </HeaderTable>

          {/* A trace that changed no entity is all lifecycle — hide the empty
              Changes block and show only the event timeline. */}
          {diffs.length > 0 && (
            <>
              <SectionTitle>Changes ({diffs.length})</SectionTitle>
              {diffs.map((diff) => {
                // A creation has no Before, a deletion no After — drop the empty
                // column rather than fill it with em-dashes.
                const showBefore = diff.kind !== 'created';
                const showAfter = diff.kind !== 'deleted';
                const valueWidth = showBefore && showAfter ? '35%' : '70%';
                return (
                  <DiffCard key={`${diff.entity}:${diff.entityId}`}>
                    <DiffHeader>
                      <KindBadge $kind={diff.kind}>{diff.kind}</KindBadge>
                      {diff.entity}{' '}
                      <EntityLink
                        to={`/admin/entities/${diff.entity}/${diff.entityId}`}
                        onClick={(e) => e.stopPropagation()}>
                        {diff.entityId}
                      </EntityLink>
                      {diff.changes.length === 0 && <HeaderNote>No properties changed</HeaderNote>}
                    </DiffHeader>
                    {diff.changes.length > 0 && (
                      <DiffTable>
                        <thead>
                          <tr>
                            <DiffTh $width="30%">Property</DiffTh>
                            {showBefore && <DiffTh $width={valueWidth}>Before</DiffTh>}
                            {showAfter && <DiffTh $width={valueWidth}>After</DiffTh>}
                          </tr>
                        </thead>
                        <tbody>
                          {diff.changes.map((change) => (
                            <tr key={change.key} data-testid={`change-${change.key}`}>
                              <DiffTd>{change.key}</DiffTd>
                              {showBefore && (
                                <DiffTd $tone="before">
                                  {change.before === null ? <Unchanged>—</Unchanged> : formatValue(change.before)}
                                </DiffTd>
                              )}
                              {showAfter && (
                                <DiffTd $tone="after">
                                  {change.after === null ? <Unchanged>—</Unchanged> : formatValue(change.after)}
                                </DiffTd>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </DiffTable>
                    )}
                  </DiffCard>
                );
              })}
            </>
          )}

          <TraceTimeline events={data.events} />
        </>
      )}
    </>
  );
}
